import {
  ESituacaoMatricula,
  EUserType,
  IAlunoPhotoDeltaId,
  IMatricula,
  IProfessor,
  IQuestionario,
  utils,
} from "@deltasge/marauders-map";

import React, { FC, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Grid,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  Typography,
} from "@mui/material";
import {
  ChevronDown as ExpandMoreIcon,
  ArrowRightThick as SituacaoIcon,
} from "mdi-material-ui";

import { Environment } from "configs";
import { Avatar } from "components/Avatar";
import { IFilterAvaliacaoQuestionario } from "interfaces";
import { useAppSelector } from "store";

export const Filter: FC<{
  Component: () => JSX.Element;
  navigate: (params: IFilterAvaliacaoQuestionario, path?: string) => void;
  filter: IFilterAvaliacaoQuestionario;
  matriculas: IMatricula[];
  professores: Partial<IProfessor>[];
  questionarios: IQuestionario[];
}> = ({
  Component,
  navigate,
  filter,
  matriculas,
  professores,
  questionarios,
}) => {
  const [panel, setPanel] = useState({
    professor: false,
    aluno: false,
    questionario: false,
  });

  const {
    usuario: { usuario },
  } = useAppSelector((state) => state);

  const professor = professores.find((f) => f.id == filter.idProfessor);

  return (
    <Box
      sx={{
        width: ({ spacing }) => spacing(Environment.DRAWER_WIDTH),
        display: "block",
      }}
    >
      <Component />
      {usuario?.tipoUsuario != EUserType.Professor && (
        <Accordion
          elevation={0}
          expanded={panel.professor}
          onChange={() =>
            setPanel((prev) => ({ ...prev, professor: !panel.professor }))
          }
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Grid container>
              <Grid item xs={12} component={Typography} variant="subtitle2">
                Professor
              </Grid>
              <Grid item xs={12} component={Typography} variant="caption">
                {!professor ? "TODOS OS PROFESSORES" : professor.nome?.rUpper()}
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              <ListItemButton
                onClick={() => {
                  navigate({
                    ...filter,
                    idProfessor: undefined,
                  });
                  setPanel((prev) => ({ ...prev, professor: false }));
                }}
              >
                <ListItemIcon>
                  <Radio checked={undefined == filter.idProfessor} />
                </ListItemIcon>
                <ListItemText primary="TODOS OS PROFESSORES" />
              </ListItemButton>
              {professores.map((p) => (
                <ListItemButton
                  key={`professor-${p.id}`}
                  onClick={() => {
                    navigate({
                      ...filter,
                      idProfessor: p.id,
                    });
                    setPanel((prev) => ({ ...prev, professor: false }));
                  }}
                >
                  <ListItemIcon>
                    <Radio checked={p.id == filter.idProfessor} />
                  </ListItemIcon>
                  <ListItemText
                    primary={p.nome?.rUpper()}
                    primaryTypographyProps={{
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}
      <Accordion
        elevation={0}
        expanded={panel.aluno}
        onChange={() => setPanel((prev) => ({ ...prev, aluno: !panel.aluno }))}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Grid container>
            <Grid item xs={12} component={Typography} variant="subtitle2">
              Aluno
            </Grid>
            <Grid item xs={12} component={Typography} variant="caption">
              {!filter.idAluno
                ? "TODOS OS ALUNOS"
                : (() => {
                    const aluno = matriculas.find(
                      (f) => f.aluno?.uid == filter.idAluno
                    )?.aluno;
                    return aluno?.nomeusual ?? aluno?.nome;
                  })()}
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <List dense>
            <ListItemButton
              onClick={() => {
                navigate({ ...filter, idAluno: undefined });
              }}
            >
              <ListItemIcon>
                <Radio checked={undefined == filter.idAluno} />
              </ListItemIcon>
              <ListItemText primary="TODOS OS ALUNOS" />
            </ListItemButton>
            {matriculas.map((m) => (
              <ListItemButton
                key={`mtricula_filter_${m.id}`}
                onClick={() => {
                  navigate({ ...filter, idAluno: m.aluno?.uid });
                }}
              >
                <ListItemIcon>
                  <Radio checked={m.aluno?.uid == filter.idAluno} />
                </ListItemIcon>
                <ListItemAvatar>
                  <Badge
                    overlap="circular"
                    badgeContent={
                      !utils.matriculaAtiva(
                        m.situacao as ESituacaoMatricula
                      ) ? (
                        <SituacaoIcon color="secondary" />
                      ) : undefined
                    }
                  >
                    <Avatar
                      key={"matricula_aluno_avatar_" + m.id}
                      displayName={m.aluno?.nomeusual?.trim() || m.aluno?.nome}
                      src={
                        m.aluno?.imagemAluno?.caminhoArquivo ??
                        (m.aluno as IAlunoPhotoDeltaId).photoUrl
                      }
                    />
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={`${m.nrChamada} - ${
                    m.aluno?.nomeusual?.trim()?.rUpper() ||
                    m.aluno?.nome?.rUpper()
                  }`}
                  primaryTypographyProps={{
                    noWrap: true,
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
      <Accordion
        elevation={0}
        expanded={panel.questionario}
        onChange={() =>
          setPanel((prev) => ({ ...prev, questionario: !panel.questionario }))
        }
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Grid container>
            <Grid item xs={12} component={Typography} variant="subtitle2">
              Questionário
            </Grid>
            <Grid item xs={12} component={Typography} variant="caption">
              {!filter.idQuestionario
                ? "TODOS OS QUESTIONÁRIOS"
                : questionarios.find((f) => f.id == filter.idQuestionario)
                    ?.nome}
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <List dense>
            <ListItemButton
              onClick={() => {
                navigate({ ...filter, idQuestionario: undefined });
              }}
            >
              <ListItemIcon>
                <Radio checked={undefined == filter.idQuestionario} />
              </ListItemIcon>
              <ListItemText primary="TODOS OS QUESTIONÁRIOS" />
            </ListItemButton>
            {questionarios.map((m) => (
              <ListItemButton
                key={`questionario_filter_${m.id}`}
                onClick={() => {
                  navigate({ ...filter, idQuestionario: m.id });
                }}
              >
                <ListItemIcon>
                  <Radio checked={m.id == filter.idQuestionario} />
                </ListItemIcon>
                <ListItemText
                  primary={m.nome}
                  primaryTypographyProps={{
                    noWrap: true,
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
